import { type AccommodationDetail } from '@/api/accommodation/types'
import { RatingInline } from '@/components/rating/RatingInline'
import { hasValidRatings } from '@/lib/rating'
import { Typography } from '@/components/ui/typography'
import { Link } from '@tanstack/react-router'
import { Baby, Bath, BedDouble, Gem, MapPin, Users } from 'lucide-react'
import { getPersonsLabel } from '@/lib/utils'
import { unitFullName } from '@/lib/unit'
import type { ComponentType, ReactNode } from 'react'

interface DetailTitleSectionProps {
  data: AccommodationDetail
}

function MetaChip({ icon: Icon, children }: { icon: ComponentType<{ className?: string }>; children: ReactNode }) {
  return (
    <div className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-3 py-1.5 text-sm text-slate-700">
      <Icon className="h-4 w-4 shrink-0 text-slate-500" />
      {children}
    </div>
  )
}

function bedroomsLabel(bedrooms: number): string {
  if (bedrooms >= 1 && bedrooms < 5) return 'ložnice'
  return 'ložnic'
}

function bathroomsLabel(bathrooms: number): string {
  if (bathrooms === 1) return 'koupelna'
  if (bathrooms < 5) return 'koupelny'
  return 'koupelen'
}

function cribsLabel(cribs: number): string {
  if (cribs === 1) return 'dětská postýlka'
  if (cribs < 5) return 'dětské postýlky'
  return 'dětských postýlek'
}

export function DetailTitleSection({ data }: DetailTitleSectionProps) {
  const { beds, bedrooms, bathrooms, cribs } = data.unit.totals

  return (
    <div>
      <Typography variant="h1" className="mb-4 capitalize">
        {unitFullName(data, data.unit)}
      </Typography>

      <div className="flex flex-wrap gap-2">
        <MetaChip icon={Users}>
          {beds || 0}&nbsp;{getPersonsLabel(beds || 0)}
        </MetaChip>
        <MetaChip icon={BedDouble}>
          {bedrooms || 0}&nbsp;{bedroomsLabel(bedrooms)}
        </MetaChip>
        {bathrooms > 0 && (
          <MetaChip icon={Bath}>
            {bathrooms}&nbsp;{bathroomsLabel(bathrooms)}
          </MetaChip>
        )}
        {cribs > 0 && (
          <MetaChip icon={Baby}>
            {cribs}&nbsp;{cribsLabel(cribs)}
          </MetaChip>
        )}
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2">
        <span className="flex items-center gap-1">
          <MapPin className="text-red h-4 w-4" />
          <Link
            className="text-red font-normal underline"
            to="."
            hash="map"
            replace
            resetScroll={false}
            hashScrollIntoView={false}
            onClick={() => document.getElementById('map')?.scrollIntoView({ behavior: 'smooth' })}
          >
            {data.region.name}
          </Link>
        </span>

        {hasValidRatings(data.sites) && <RatingInline sites={data.sites} />}
      </div>

      <p className="mt-4 overflow-hidden text-slate-600">{data.description.short}</p>

      {data.rareFind && (
        <div className="mt-4 flex max-w-xl items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
          <Gem className="mt-0.5 h-[18px] w-[18px] shrink-0 text-amber-600" />
          <div>
            <div className="text-sm font-semibold text-amber-800">Vzácný objev</div>
            <div className="text-sm text-slate-600">
              Tento objekt bývá obvykle plně obsazený. Teď se v kalendáři výjimečně uvolnil termín.
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
